/* ==========================================================================
   Executive Beverage — Form confirmations
   --------------------------------------------------------------------------
   ONE confirmation mechanism for every form on the site: what you sent, a
   reference to quote, what happens next, and a way to keep a copy.

   WHY IT WORKS THIS WAY
   A Shopify contact form POSTs to /contact and redirects back to the page with
   form.posted_successfully? set — but WITHOUT the submitted values. Liquid
   cannot render "here is what you sent", because by then it no longer knows.
   So the summary is captured in the browser a moment before submit and painted
   on the way back. sessionStorage rather than localStorage: a confirmation is
   for this visit, not forever.

   THE REFERENCE NUMBER is generated at capture and pushed into a hidden field
   in the same breath, so the number on the customer's screen is the number in
   the office's email. A reference only one side can see is no use to either.

   Fields are grouped under whatever heading they sit beneath in the form, and
   empty ones are dropped — the read-out mirrors the shape of the form the
   person just filled in, with none of the blanks.

   MARKUP CONTRACT
     On the form:   data-eb-confirm-form="crush-kit"
     On the panel:  data-eb-confirm="crush-kit"   (rendered by eb-confirm snippet)
   Anything with data-eb-confirm-skip on it, or a name we treat as plumbing, is
   left out of the read-out.

   Portable: plain ES5-compatible DOM code, no dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var STORE_PREFIX = 'ebConfirm:';
  var SKIP_NAMES = /^(form_type|utf8|tags|form_key|g-recaptcha-response|h-captcha-response)$/;

  /* ------------------------------------------------------------- reference */

  function makeReference() {
    var d = new Date();
    var stamp =
      String(d.getFullYear()).slice(2) +
      ('0' + (d.getMonth() + 1)).slice(-2) +
      ('0' + d.getDate()).slice(-2);
    var tail = Math.floor(Math.random() * 1296).toString(36).toUpperCase();
    while (tail.length < 2) tail = '0' + tail;
    return 'EB-' + stamp + '-' + tail;
  }

  /* ---------------------------------------------------------------- labels */

  function labelFor(el) {
    if (el.id) {
      var byFor = document.querySelector('label[for="' + el.id + '"]');
      if (byFor) return clean(byFor.textContent);
    }
    var wrap = el.closest('label');
    if (wrap) return clean(wrap.textContent);
    var name = el.getAttribute('name') || '';
    // contact[Total guests] -> Total guests
    var m = /\[([^\]]+)\]/.exec(name);
    return m ? m[1] : name;
  }

  function clean(s) {
    return String(s || '').replace(/\s+/g, ' ').trim();
  }

  // The heading the field sits under, so the read-out keeps the form's shape.
  function groupFor(el, form) {
    var node = el;
    while (node && node !== form) {
      var sib = node.previousElementSibling;
      while (sib) {
        if (sib.matches('h2, h3, h4, legend, .eb-est__sublabel, .eb-est__step-title')) {
          return clean(sib.textContent);
        }
        var inner = sib.querySelector && sib.querySelector('h2, h3, h4, legend, .eb-est__sublabel');
        if (inner) return clean(inner.textContent);
        sib = sib.previousElementSibling;
      }
      node = node.parentElement;
    }
    return '';
  }

  /* --------------------------------------------------------------- capture */

  function capture(form) {
    var slug = form.getAttribute('data-eb-confirm-form');
    if (!slug) return null;

    var reference = makeReference();

    // Put the reference where the office will see it too.
    var refField = form.querySelector('[data-eb-confirm-ref]');
    if (!refField) {
      refField = document.createElement('input');
      refField.type = 'hidden';
      refField.name = 'contact[Reference]';
      refField.setAttribute('data-eb-confirm-ref', '');
      form.appendChild(refField);
    }
    refField.value = reference;

    var rows = [];
    var seenRadio = {};
    var fields = form.querySelectorAll('input, select, textarea');

    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      var name = el.getAttribute('name') || '';
      if (!name || el.disabled) continue;
      if (el.hasAttribute('data-eb-confirm-skip')) continue;
      if (SKIP_NAMES.test(name)) continue;
      if (name === 'contact[Reference]') continue;

      var value = '';
      if (el.type === 'checkbox') {
        if (!el.checked) continue;
        value = el.value && el.value !== 'on' ? el.value : 'Yes';
      } else if (el.type === 'radio') {
        if (!el.checked) continue;
        if (seenRadio[name]) continue;
        seenRadio[name] = true;
        value = el.value;
      } else {
        value = clean(el.value);
        if (!value) continue;
      }

      /* A hidden field holding a pre-built summary is for the office, not for
         a read-out — it would repeat the whole thing back as one wall of text. */
      if (el.tagName === 'TEXTAREA' && el.classList.contains('eb-visually-hidden')) continue;
      if (el.type === 'hidden' && value.length > 120) continue;

      rows.push({
        group: groupFor(el, form),
        label: labelFor(el),
        value: value,
        multi: el.type === 'checkbox'
      });
    }

    var payload = { reference: reference, at: new Date().toISOString(), rows: rows };
    try {
      sessionStorage.setItem(STORE_PREFIX + slug, JSON.stringify(payload));
    } catch (e) {
      /* Private browsing, or storage full. The confirmation still renders, just
         without the read-out — better than blocking the submit. */
    }
    return payload;
  }

  /* ----------------------------------------------------------------- paint */

  function paint(panel) {
    var slug = panel.getAttribute('data-eb-confirm');
    var raw = null;
    try {
      raw = sessionStorage.getItem(STORE_PREFIX + slug);
    } catch (e) { /* nothing to read */ }

    var refOut = panel.querySelector('[data-eb-confirm-reference]');
    var listOut = panel.querySelector('[data-eb-confirm-rows]');
    var emptyOut = panel.querySelector('[data-eb-confirm-empty]');
    var stampOut = panel.querySelector('[data-eb-confirm-stamp]');

    if (!raw) {
      // Landed here without a captured submit — a reload, or a shared URL.
      if (emptyOut) emptyOut.hidden = false;
      if (listOut) listOut.hidden = true;
      return;
    }

    var data;
    try { data = JSON.parse(raw); } catch (e) { return; }

    if (refOut && data.reference) refOut.textContent = data.reference;
    if (stampOut && data.at) {
      var d = new Date(data.at);
      stampOut.textContent = d.toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric'
      }) + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }

    if (!listOut) return;
    listOut.textContent = '';
    var currentGroup = null;
    var wrote = 0;

    for (var i = 0; i < (data.rows || []).length; i++) {
      var row = data.rows[i];
      if (row.group && row.group !== currentGroup) {
        currentGroup = row.group;
        var h = document.createElement('p');
        h.className = 'eb-confirm__group';
        h.textContent = currentGroup;
        listOut.appendChild(h);
      }
      var dl = document.createElement('div');
      dl.className = 'eb-confirm__row';
      var k = document.createElement('span');
      k.className = 'eb-confirm__key';
      k.textContent = row.label;
      var v = document.createElement('span');
      v.className = 'eb-confirm__val';
      v.textContent = row.value;
      dl.appendChild(k);
      dl.appendChild(v);
      listOut.appendChild(dl);
      wrote++;
    }

    if (!wrote && emptyOut) emptyOut.hidden = false;
  }

  /* --------------------------------------------------------------- actions */

  function plainText(panel) {
    var out = [];
    var ref = panel.querySelector('[data-eb-confirm-reference]');
    var title = panel.querySelector('[data-eb-confirm-title]');
    if (title) out.push(clean(title.textContent));
    if (ref) out.push('Reference: ' + clean(ref.textContent));
    out.push('');
    var nodes = panel.querySelectorAll('.eb-confirm__group, .eb-confirm__row');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.classList.contains('eb-confirm__group')) {
        out.push('');
        out.push(clean(n.textContent).toUpperCase());
      } else {
        var k = n.querySelector('.eb-confirm__key');
        var v = n.querySelector('.eb-confirm__val');
        out.push('  ' + clean(k && k.textContent) + ': ' + clean(v && v.textContent));
      }
    }
    out.push('');
    out.push('Executive Beverage · events@execbeverage.com · (757) 995-5855');
    return out.join('\n');
  }

  function wireActions(panel) {
    var printBtn = panel.querySelector('[data-eb-confirm-print]');
    if (printBtn) {
      printBtn.addEventListener('click', function () { window.print(); });
    }

    var copyBtn = panel.querySelector('[data-eb-confirm-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var text = plainText(panel);
        var done = function () {
          var was = copyBtn.textContent;
          copyBtn.textContent = 'Copied';
          setTimeout(function () { copyBtn.textContent = was; }, 1800);
        };
        var fallback = function () {
          var ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.style.cssText = 'position:fixed;left:-9999px;top:0';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); done(); } catch (e) { /* nothing useful to do */ }
          document.body.removeChild(ta);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, fallback);
        } else {
          fallback();
        }
      });
    }
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    /* Capture in the BUBBLE phase on document, so it runs after the guards that
       may cancel the submit (eb-limits, the estimate wizard's step gate).

       The test is the form's own validity, NOT event.defaultPrevented. A
       document-level handler on this store cancels the default on every contact
       form and posts it over AJAX instead, so defaultPrevented is true even on a
       submission that is about to succeed — using it as the signal meant nothing
       was ever captured. checkValidity() asks the only question that matters:
       is this form actually complete? */
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.getAttribute || !form.getAttribute('data-eb-confirm-form')) return;
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
      capture(form);
    });

    var panels = document.querySelectorAll('[data-eb-confirm]');
    for (var i = 0; i < panels.length; i++) {
      paint(panels[i]);
      wireActions(panels[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
